import api from './api';

/**
 * Shared bulk-template upload runner.
 *
 * Every "Upload Template" screen (customers, vendors, goods items, service
 * items, employees, ...) funnels its POST through submitTemplateFile so the
 * user always gets:
 *   - a progress modal while the file is transmitted,
 *   - a "still importing rows" phase while the server processes the workbook
 *     (timeouts are paused — the client sends timeout: 0 and the backend keeps
 *     the socket alive for the whole import),
 *   - a final status comparison (total rows / created / updated / skipped /
 *     errors + row-level issues).
 *
 * The modal lives in <TemplateUploadProgressModal /> (mounted once in the app
 * layout). Communication happens over window CustomEvents so any page/panel can
 * start an upload without wiring per-page modal state.
 */

export const TEMPLATE_UPLOAD_EVENT = 'dira360:template-upload';

const dispatchUploadEvent = (type, detail = {}) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TEMPLATE_UPLOAD_EVENT, {
    detail: { type, ...detail }
  }));
};

const toCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const toIssueStrings = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (!entry || typeof entry !== 'object') return '';
      if (typeof entry.reason === 'string') {
        const row = entry.row ? `Row ${entry.row}: ` : '';
        return `${row}${entry.reason}`;
      }
      if (typeof entry.message === 'string') return entry.message;
      return '';
    })
    .filter(Boolean);
};

/**
 * Normalises the different backend upload summary payloads into one shape the
 * progress/result modal can render as a status comparison.
 *
 * Backend payloads in use:
 *   customers/vendors  -> { totalRows, created, updated, skipped, issues[] }
 *   goods/services     -> { totalRows, created, updated, skipped, categoriesCreated, uomsCreated, issues[] }
 *   employees          -> { total, created, updated, skipped, failed, failures[] }
 */
export const normalizeUploadSummary = (data = {}) => {
  const created = toCount(data.created);
  const updated = toCount(data.updated);
  const failed = toCount(data.failed);
  const skipped = toCount(data.skipped);

  let total = data.totalRows !== undefined ? toCount(data.totalRows) : toCount(data.total);
  if (total <= 0) {
    total = created + updated + skipped + failed;
  }

  const issues = toIssueStrings(data.issues)
    .concat(toIssueStrings(data.failures))
    .concat(toIssueStrings(data.errors));

  return {
    total,
    created,
    updated,
    skipped: skipped + failed,
    errorCount: issues.length,
    issues,
    extra: {
      categoriesCreated: toCount(data.categoriesCreated),
      uomsCreated: toCount(data.uomsCreated),
      adjustmentsCreated: Array.isArray(data.adjustmentsCreated) ? data.adjustmentsCreated.length : toCount(data.adjustmentsCreated)
    }
  };
};

const extractErrorMessage = (error) => {
  if (!error) return 'Upload failed. Please try again.';
  const serverMessage = error?.response?.data?.message
    || error?.response?.data?.error
    || (Array.isArray(error?.response?.data?.errors) && error.response.data.errors[0])
    || error?.message;
  if (serverMessage) return String(serverMessage);
  if (error?.code === 'ECONNABORTED') return 'The upload timed out. Please try a smaller file or fewer rows.';
  return 'Upload failed. Check your connection and try again.';
};

/**
 * A gateway/server cut (HTTP 408/502/504 or a network error while waiting) does
 * NOT mean the import failed: reverse proxies often kill a long-running request
 * while the backend keeps importing and committing rows in the background. We
 * treat those as "uncertain" outcomes so the UI does not tell the user nothing
 * was changed when rows may actually have been saved.
 */
const UNCERTAIN_STATUSES = new Set([408, 502, 504]);
const UNCERTAIN_ERROR_CODES = new Set([
  'ECONNABORTED',
  'ECONNRESET',
  'ERR_NETWORK',
  'ERR_EMPTY_RESPONSE',
  'ERR_BAD_RESPONSE',
  'UND_ERR_SOCKET',
  'ETIMEDOUT'
]);

export const isUncertainUploadFailure = (error) => {
  if (!error) return false;
  if (UNCERTAIN_STATUSES.has(Number(error?.response?.status))) return true;
  return !error?.response && UNCERTAIN_ERROR_CODES.has(String(error?.code || ''));
};

const describeUploadFailure = (error) => {
  const uncertain = isUncertainUploadFailure(error);
  if (!uncertain) {
    return { uncertain: false, message: extractErrorMessage(error) };
  }

  const status = Number(error?.response?.status);
  const detail = status
    ? `HTTP ${status}`
    : (String(error?.code || '').toLowerCase() || 'a network error');

  return {
    uncertain: true,
    message: `The server didn't reply before the connection was closed (${detail}). ` +
      'The import may still have finished in the background.'
  };
};

/**
 * Uploads a filled-in template file with a visible progress modal.
 *
 * @param {object}   options
 * @param {File}     options.file         The .xlsx/.xls file chosen by the user.
 * @param {string}   options.endpoint     API path, e.g. '/shop/customers/upload'.
 * @param {string}   options.entityLabel  Human label shown in the modal ("Customers").
 * @returns {Promise} resolves with the axios response once the import completes.
 */
export const submitTemplateFile = ({ file, endpoint, entityLabel = 'Items' }) => {
  if (!file) return Promise.reject(new Error('No file selected.'));
  const url = String(endpoint || '').trim();
  if (!url) return Promise.reject(new Error('Upload endpoint is missing.'));

  const startedAt = Date.now();
  dispatchUploadEvent('start', {
    fileName: file.name,
    fileSize: file.size || 0,
    entityLabel
  });

  const formData = new FormData();
  formData.append('file', file);

  let uploadFinished = false;
  return api.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // Client-side timeout is paused for the duration of the import; the request
    // may only finish once the server has imported every row in the workbook.
    timeout: 0,
    meta: { suppressToast: true },
    onUploadProgress: (event) => {
      const total = Number(event?.total) || 0;
      const loaded = Number(event?.loaded) || 0;
      if (!uploadFinished && total > 0 && loaded >= total) {
        uploadFinished = true;
        dispatchUploadEvent('processing', { startedAt });
        return;
      }
      dispatchUploadEvent('progress', { loaded, total, startedAt });
    }
  }).then((response) => {
    dispatchUploadEvent('result', {
      startedAt,
      summary: normalizeUploadSummary(response?.data?.data)
    });
    return response;
  }).catch((error) => {
    const failure = describeUploadFailure(error);
    dispatchUploadEvent('error', {
      message: failure.message,
      uncertain: failure.uncertain,
      statusCode: Number(error?.response?.status) || 0
    });
    throw error;
  });
};

export default submitTemplateFile;
